import { SEVERITY_LABELS } from '@fos/shared/labels';
import { ChevronDown, ListPlus, Plus, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { useAccess } from '../../lib/useAccess';
import { Button } from '../../ui/Button';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '../../ui/Menu';
import { TasksFromTextDialog, useCopilotStatus } from '../copilot';
import type { InboxMessageViewProps } from '../inbox';
import { type ThreadActionsProps, ThreadView } from '../mail';
import { NewTaskDialog } from '../tasks';

/**
 * Kompozycja Fali 4 — wiadomość w Inboxie (Figma 02 „Detail”): pozycja z wątkiem poczty pokazuje wątek (`ThreadView`:
 * nadawca, treść, baner „Nieznany nadawca…” z przypisaniem albo „Nowy klient”, szkice, blok Copilota „Mogę
 * przygotować odpowiedź”, „Odpowiedz”) z akcjami pozycji Inboxu (Zrobione, Odłóż, menu „…”). Akcje wątku
 * (`ThreadTaskActions`, slot poczty — także na stronie wątku i w profilu klienta): „Utwórz zadanie” — ręcznie (bez AI)
 * albo, gdy AI działa, z menu „Zadania z treści (AI)” (propozycje do przeglądu). Pod spodem: powód klasyfikacji
 * i powiązania inne niż wątek i klient (np. incydent, do którego dołączono zgłoszenie).
 */

/** Wątek i klient są w nagłówku wątku — w powiązaniach pod spodem tylko pozostałe rekordy. */
const SHOWN_IN_THREAD = new Set(['message_thread', 'message', 'client']);

/**
 * „Utwórz zadanie” z wiadomości: formularz zadania z tematem i klientem wątku, pochodzenie „Z wiadomości”. Gdy AI
 * działa — przycisk z menu: „Nowe zadanie…” albo „Zadania z treści (AI)…” (jeden przycisk zamiast dwóch: wiersz akcji
 * w podglądzie Inboxu mieści się w 640 px, Figma 02: Odpowiedz, Utwórz zadanie, Zrobione).
 */
export function CreateTaskFromMessageButton({
  messageId,
  title,
  clientId,
  sourceLabel,
}: {
  messageId: string;
  title: string;
  clientId: string | null;
  sourceLabel?: string;
}) {
  const access = useAccess();
  const canTasks = access.can('tasks', 'edit');
  const status = useCopilotStatus(canTasks);
  const [open, setOpen] = useState<'manual' | 'ai' | null>(null);
  if (!canTasks) return null;
  const withAi = status.data?.available === true;
  return (
    <>
      {withAi ? (
        <Menu>
          <MenuTrigger asChild>
            <Button variant="secondary" icon={ListPlus}>
              Utwórz zadanie
              <ChevronDown size={14} strokeWidth={1.75} aria-hidden className="-mr-1" />
            </Button>
          </MenuTrigger>
          <MenuContent align="start" className="w-60">
            <MenuItem icon={Plus} onSelect={() => setOpen('manual')}>
              Nowe zadanie…
            </MenuItem>
            <MenuItem icon={Sparkles} onSelect={() => setOpen('ai')}>
              Zadania z treści (AI)…
            </MenuItem>
          </MenuContent>
        </Menu>
      ) : (
        <Button variant="secondary" icon={ListPlus} onClick={() => setOpen('manual')}>
          Utwórz zadanie
        </Button>
      )}
      <NewTaskDialog
        open={open === 'manual'}
        onOpenChange={(o) => setOpen(o ? 'manual' : null)}
        defaultClientId={clientId}
        defaultTitle={title}
        sourceMessage={{ id: messageId, label: sourceLabel }}
      />
      {withAi && (
        <TasksFromTextDialog
          open={open === 'ai'}
          onOpenChange={(o) => setOpen(o ? 'ai' : null)}
          messageId={messageId}
          sourceLabel={sourceLabel}
        />
      )}
    </>
  );
}

/** Akcje wątku poczty: zadanie z ostatniej wiadomości od klienta (ręcznie albo z AI). */
export function ThreadTaskActions({ thread }: ThreadActionsProps) {
  const source = [...thread.messages].reverse().find((m) => m.direction === 'in') ?? thread.messages.at(-1);
  if (!source) return null;
  const sourceLabel = `wiadomość od ${source.from.name ?? source.from.email}`;
  return (
    <CreateTaskFromMessageButton
      messageId={source.id}
      title={thread.subject}
      clientId={thread.client?.id ?? null}
      sourceLabel={sourceLabel}
    />
  );
}

export function InboxMessageView({ item, menu, actions, banners }: InboxMessageViewProps) {
  const e = item.event;
  const others = item.links.filter((l) => !SHOWN_IN_THREAD.has(l.type));
  const reason = e.reason && !e.simulated ? e.reason : null;

  const footer =
    reason || e.override || others.length > 0 ? (
      <div className="mt-6 flex flex-col gap-4">
        {(reason || e.override) && (
          <div className="flex flex-col gap-1 text-caption text-fg-muted">
            {e.override && (
              <p>
                Ważność zmieniona z „{SEVERITY_LABELS[e.originalSeverity]}” na „{SEVERITY_LABELS[e.severity]}”
                {e.override.by ? ` · ${e.override.by.name}` : ''}
                {item.overrideReason ? ` · ${item.overrideReason}` : ''}
              </p>
            )}
            {reason && <p>Powód klasyfikacji: {reason}</p>}
          </div>
        )}
        {others.length > 0 && (
          <section aria-label="Powiązania">
            <h3 className="text-caption font-medium text-fg-muted">Powiązania</h3>
            <ul className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-body-s">
              {others.map((l) => (
                <li key={`${l.type}:${l.id}`}>
                  {l.path ? (
                    <Link
                      to={l.path}
                      className="rounded-xs text-fg-secondary underline-offset-2 hover:text-fg hover:underline focus-visible:outline-2 focus-visible:outline-accent"
                    >
                      {l.label}
                    </Link>
                  ) : (
                    <span className="text-fg-secondary">{l.label}</span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    ) : null;

  return (
    <ThreadView
      threadId={e.threadId as string}
      headerAction={menu}
      notice={banners}
      footer={footer}
      actions={actions}
    />
  );
}
